import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Search, Filter, Calendar, MapPin, User, FileText, X, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function SearchPage() {
  const nav = useNavigate();
  const [interventions, setInterventions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    technician: '',
    hasFranchise: false
  });
  
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, interventions]);

  const fetchData = async () => {
    try {
      const [intRes, techRes] = await Promise.all([
        axios.get(`${API}/interventions`),
        axios.get(`${API}/users/technicians`).catch(() => ({ data: [] }))
      ]);
      setInterventions(intRes.data || []);
      setFiltered(intRes.data || []);
      setTechnicians(techRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...interventions];
    
    // Recherche texte
    if (filters.search) {
      const s = filters.search.toLowerCase();
      result = result.filter(i => 
        i.adresse_complete?.toLowerCase().includes(s) ||
        i.client_nom?.toLowerCase().includes(s) ||
        i.technician_name?.toLowerCase().includes(s) ||
        i.resume_constats?.toLowerCase().includes(s) ||
        i.origine_probable?.toLowerCase().includes(s)
      );
    }
    
    // Date de début
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom);
      result = result.filter(i => new Date(i.created_at) >= from);
    }
    
    // Date de fin
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      to.setHours(23, 59, 59);
      result = result.filter(i => new Date(i.created_at) <= to);
    }
    
    // Technicien
    if (filters.technician) {
      result = result.filter(i => i.technician_id === filters.technician);
    }
    
    // Franchise
    if (filters.hasFranchise) {
      result = result.filter(i => i.franchise);
    }
    
    setFiltered(result);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      dateFrom: '',
      dateTo: '',
      technician: '',
      hasFranchise: false
    });
  };

  const exportResults = () => {
    const csv = [
      ['Date', 'Adresse', 'Client', 'Technicien', 'Franchise', 'Montant'].join(';'),
      ...filtered.map(i => [
        new Date(i.created_at).toLocaleDateString('fr-FR'),
        i.adresse_complete || '',
        i.client_nom || '',
        i.technician_name || '',
        i.franchise ? 'Oui' : 'Non',
        i.franchise_montant || ''
      ].join(';'))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `kardif_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Export CSV téléchargé');
  };

  const activeFiltersCount = [
    filters.dateFrom,
    filters.dateTo,
    filters.technician,
    filters.hasFranchise
  ].filter(Boolean).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] dark:bg-[#1A1A1A]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#8B7355]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] dark:bg-[#1A1A1A]">
      {/* Header */}
      <header className="bg-white dark:bg-[#2D2D2D] shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => nav(-1)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-bold text-[#1A1A1A] dark:text-white flex items-center gap-2">
                <Search className="h-6 w-6 text-[#8B7355]" />
                Recherche
              </h1>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filtres
                {activeFiltersCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-[#8B7355] text-white text-xs h-5 w-5 flex items-center justify-center rounded-full p-0">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
              <Button onClick={exportResults} className="bg-[#8B7355] hover:bg-[#7A6548]">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
          
          {/* Barre de recherche */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input 
              placeholder="Rechercher par adresse, client, technicien..." 
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 h-12"
            />
          </div>
          
          {/* Filtres */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-[#1A1A1A] rounded-xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm">Date début</Label>
                  <Input 
                    type="date" 
                    value={filters.dateFrom} 
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Date fin</Label>
                  <Input 
                    type="date" 
                    value={filters.dateTo} 
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Technicien</Label>
                  <select 
                    value={filters.technician}
                    onChange={(e) => setFilters({ ...filters, technician: e.target.value })}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background"
                  >
                    <option value="">Tous</option>
                    {technicians.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={filters.hasFranchise}
                      onChange={(e) => setFilters({ ...filters, hasFranchise: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Avec franchise</span>
                  </label>
                </div>
              </div>
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="mt-3 text-red-500">
                  <X className="h-4 w-4 mr-1" />
                  Effacer les filtres
                </Button>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <p className="text-sm text-[#6B7280] mb-4">{filtered.length} résultat(s)</p>
        
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <Card className="dark:bg-[#2D2D2D]">
              <CardContent className="p-12 text-center">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#6B7280]">Aucun résultat trouvé</p>
              </CardContent>
            </Card>
          ) : (
            filtered.map(i => (
              <Card 
                key={i.id} 
                className="dark:bg-[#2D2D2D] cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => nav(`/intervention/${i.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="h-4 w-4 text-[#8B7355]" />
                        <span className="text-sm text-[#6B7280]">
                          {new Date(i.created_at).toLocaleDateString('fr-FR', { 
                            day: '2-digit', month: 'short', year: 'numeric' 
                          })}
                        </span>
                        {i.franchise && (
                          <Badge className="bg-amber-100 text-amber-800 text-xs">
                            Franchise {i.franchise_montant || ''}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-[#1A1A1A] dark:text-white flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[#8B7355]" />
                        {i.adresse_complete || 'Adresse non renseignée'}
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#6B7280]">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {i.technician_name || 'N/A'}
                        </span>
                        {i.client_nom && (
                          <span>Client: {i.client_nom}</span>
                        )}
                      </div>
                    </div>
                    <FileText className="h-5 w-5 text-[#8B7355]" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
}

export default SearchPage;
